/**
 * useOrderSubscription Hook
 * Subscribe to real-time order updates via Socket.IO
 * (TypeScript since ST-00216)
 */

import { useEffect, useRef } from 'react';
import type { Socket } from 'socket.io-client';
import { connectSocket } from '../services/socketClient';
import { maskResponseData } from '../utils/demoMode';

let socket: Socket | null = null;

const ORDER_EVENTS = [
  'order:created',
  'order:updated',
  'order:status:changed',
  'order:completed',
  'order:cancelled',
  'order:amended',
  'order:lineitem:added',
  'order:lineitem:updated',
  'order:lineitem:deleted',
  'order:dashboard:refresh',
] as const;

const CALLBACK_KEYS = [
  'onOrderCreated',
  'onOrderUpdated',
  'onStatusChanged',
  'onOrderCompleted',
  'onOrderCancelled',
  'onOrderAmended',
  'onLineItemAdded',
  'onLineItemUpdated',
  'onLineItemDeleted',
  'onDashboardRefresh',
] as const;

/** One callback per order:* event (CALLBACK_KEYS[i] handles ORDER_EVENTS[i]); each gets the event's payload. */
export type OrderSubscriptionCallbacks = Partial<
  Record<(typeof CALLBACK_KEYS)[number], (...args: unknown[]) => void>
>;

const useOrderSubscription = (callbacks: OrderSubscriptionCallbacks = {}): Socket | null => {
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  useEffect(() => {
    if (!socket) {
      // Authenticated by the session cookie; the server puts this socket in the
      // user's own tenant room, so order:* events arrive only for that tenant
      // (ST-00214). connectSocket refreshes an expired session and reconnects.
      socket = connectSocket('');

      socket.on('connect', () => {
        console.log('✓ Connected to Order Management Socket.IO');
      });

      socket.on('disconnect', () => {
        console.log('✗ Disconnected from Order Management Socket.IO');
      });

      socket.on('connect_error', (error) => {
        console.error('Socket.IO connection error:', error);
      });
    }

    // Keep a stable handler per event so cleanup detaches only this consumer's
    // listeners. A bare socket.off(event) drops every listener on the shared
    // socket, which unsubscribes any other mounted consumer too.
    const handlers = ORDER_EVENTS.map((_event, i) => (...args: unknown[]) => {
      const fn = callbacksRef.current[CALLBACK_KEYS[i]!];
      // Demo mode masks live payloads like API responses: order details show `data.order`
      // as it arrives (PLT-12, ST-00230). A no-op outside demo mode.
      if (fn) fn(...args.map((arg) => maskResponseData(arg)));
    });

    // The shared socket exists from here on (it is created above and never cleared).
    ORDER_EVENTS.forEach((event, i) => socket!.on(event, handlers[i]!));

    return () => {
      ORDER_EVENTS.forEach((event, i) => socket!.off(event, handlers[i]!));
    };
  }, []);

  return socket;
};

export default useOrderSubscription;
